import React, { createContext, useContext, useEffect, useState, useCallback, useRef, useMemo } from 'react';
import syntheticsWebSocketService from '../services/syntheticsWebSocketService';
import { useAuth } from './AuthContext';

/**
 * Context for managing WebSocket connection and real-time updates for Synthetics.
 */
const SyntheticsWebSocketContext = createContext(null);

/**
 * Hook to access the Synthetics WebSocket context.
 * Calling this hook activates the WebSocket connection (lazy connect).
 */
export const useSyntheticsWebSocket = () => {
  const context = useContext(SyntheticsWebSocketContext);
  if (!context) {
    throw new Error('useSyntheticsWebSocket must be used within a SyntheticsWebSocketProvider');
  }
  // Signal to the provider that a consumer is mounted — activate the connection
  useEffect(() => {
    context._activate();
    return () => context._deactivate();
  }, [context]);
  return context;
};

/**
 * Provider component for Synthetics WebSocket functionality.
 */
export const SyntheticsWebSocketProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [connected, setConnected] = useState(false);
  const [runningExecutions, setRunningExecutions] = useState(new Map());
  const [recentUpdates, setRecentUpdates] = useState([]);
  const updateListenersRef = useRef(new Set());
  const consumerCountRef = useRef(0);
  const [active, setActive] = useState(false);

  // Activation methods — called by useSyntheticsWebSocket hook
  const activate = useCallback(() => {
    consumerCountRef.current += 1;
    if (consumerCountRef.current === 1) setActive(true);
  }, []);

  const deactivate = useCallback(() => {
    consumerCountRef.current = Math.max(0, consumerCountRef.current - 1);
    if (consumerCountRef.current === 0) setActive(false);
  }, []);

  // Only connect when authenticated AND at least one consumer is mounted
  useEffect(() => {
    if (isAuthenticated && active) {
      syntheticsWebSocketService.connect().catch((error) => {
        console.error('[SyntheticsWS] Failed to connect:', error);
      });

      // Listen for connection state changes
      const unsubscribe = syntheticsWebSocketService.addConnectionListener((isConnected) => {
        setConnected(isConnected);
      });

      return () => {
        unsubscribe();
        syntheticsWebSocketService.disconnect();
      };
    } else {
      syntheticsWebSocketService.disconnect();
      setConnected(false);
    }
  }, [isAuthenticated, active]);

  // Subscribe to execution updates
  useEffect(() => {
    if (!connected) return;

    const unsubscribe = syntheticsWebSocketService.onExecutionUpdate((update) => {
      console.log('[SyntheticsWS] Execution update:', update);

      // Update running executions map
      setRunningExecutions((prev) => {
        const newMap = new Map(prev);
        const { executionId, eventType, status } = update;

        if (eventType === 'SUBMITTED' || status === 'RUNNING' || status === 'SUBMITTED') {
          newMap.set(executionId, update);
        } else if (eventType === 'COMPLETED' || eventType === 'FAILED' ||
                   status === 'COMPLETED' || status === 'FAILED') {
          newMap.delete(executionId);
        } else if (eventType === 'PROGRESS') {
          const existing = newMap.get(executionId);
          newMap.set(executionId, { ...existing, ...update });
        }

        return newMap;
      });

      // Add to recent updates (keep last 50)
      setRecentUpdates((prev) => {
        const newUpdates = [{ ...update, receivedAt: Date.now() }, ...prev];
        return newUpdates.slice(0, 50);
      });

      // Notify all registered listeners
      updateListenersRef.current.forEach((listener) => {
        try {
          listener(update);
        } catch (e) {
          console.error('[SyntheticsWS] Error in update listener:', e);
        }
      });
    });

    return () => {
      unsubscribe();
    };
  }, [connected]);

  /**
   * Subscribe to execution updates with a callback.
   * Returns an unsubscribe function.
   */
  const onExecutionUpdate = useCallback((callback) => {
    updateListenersRef.current.add(callback);
    return () => {
      updateListenersRef.current.delete(callback);
    };
  }, []);

  /**
   * Subscribe to updates for a specific execution.
   */
  const subscribeToExecution = useCallback((executionId, callback) => {
    return syntheticsWebSocketService.onExecutionById(executionId, callback);
  }, []);

  /**
   * Subscribe to updates for a specific test.
   */
  const subscribeToTest = useCallback((testId, callback) => {
    return syntheticsWebSocketService.onTestExecutions(testId, callback);
  }, []);

  /**
   * Subscribe to updates for a specific agent.
   */
  const subscribeToAgent = useCallback((agentId, callback) => {
    return syntheticsWebSocketService.onAgentExecutions(agentId, callback);
  }, []);

  /**
   * Get all currently running executions.
   */
  const getRunningExecutions = useCallback(() => {
    return Array.from(runningExecutions.values());
  }, [runningExecutions]);

  /**
   * Check if a specific execution is running.
   */
  const isExecutionRunning = useCallback((executionId) => {
    return runningExecutions.has(executionId);
  }, [runningExecutions]);

  /**
   * Get the status of a running execution.
   */
  const getExecutionStatus = useCallback((executionId) => {
    return runningExecutions.get(executionId);
  }, [runningExecutions]);

  /**
   * Clear recent updates.
   */
  const clearRecentUpdates = useCallback(() => {
    setRecentUpdates([]);
  }, []);

  const runningExecutionsList = useMemo(
    () => Array.from(runningExecutions.values()),
    [runningExecutions],
  );

  const value = useMemo(() => ({
    // Connection state
    connected,
    isConnected: connected,
    isPolling: syntheticsWebSocketService.isPolling(),

    // Running executions
    runningExecutions: runningExecutionsList,
    runningExecutionCount: runningExecutionsList.length,
    getRunningExecutions,
    isExecutionRunning,
    getExecutionStatus,

    // Recent updates
    recentUpdates,
    clearRecentUpdates,

    // Subscription methods
    onExecutionUpdate,
    subscribeToExecution,
    subscribeToTest,
    subscribeToAgent,

    // Direct service access (for advanced use cases)
    service: syntheticsWebSocketService,

    // Internal: lazy activation (called by useSyntheticsWebSocket hook)
    _activate: activate,
    _deactivate: deactivate,
  }), [
    connected,
    runningExecutionsList,
    recentUpdates,
    getRunningExecutions,
    isExecutionRunning,
    getExecutionStatus,
    clearRecentUpdates,
    onExecutionUpdate,
    subscribeToExecution,
    subscribeToTest,
    subscribeToAgent,
    activate,
    deactivate,
  ]);

  return (
    <SyntheticsWebSocketContext.Provider value={value}>
      {children}
    </SyntheticsWebSocketContext.Provider>
  );
};

export default SyntheticsWebSocketContext;
